import styled from "@emotion/styled/macro";
import {
  Skeleton,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { OfferReviewComparisonInfographicCard } from "components/organisms/offerReviewComparison/OfferReviewComparisonInfographicCard";
import {
  CAP_COMPARISON_FEATURE_LIST,
  CAP_SOLAR_RECAP,
  CAP_UTILITY_RECAP,
} from "components/organisms/offerReviewComparison/offerReviewComparisonContent";

type CapDesignStepComparisonInfographicsProps = {
  mode: "single" | "comparison";
  activeSelfSupplyPercent: number;
  originalSelfSupplyPercent: number;
  activeProposalId?: string;
  originalProposalId?: string;
  utilityCompany?: string;
  isLoading: boolean;
};

const CapDesignStepComparisonInfographics = ({
  mode,
  activeSelfSupplyPercent,
  originalSelfSupplyPercent,
  activeProposalId = "",
  originalProposalId = "",
  utilityCompany = "",
  isLoading,
}: CapDesignStepComparisonInfographicsProps) => {
  const utility = CAP_UTILITY_RECAP;
  const isMobile = useSingletonMobileMediaQuery();
  const beforeSunrunSection = (
    <InfographicSection>
      <SectionTitle
        size={32}
        fontWeight="regular"
        color={tokens.BRAND_HERO_BLUE}
      >
        Before Sunrun
      </SectionTitle>
      <OfferReviewComparisonInfographicCard
        variant="utility"
        layout={isMobile ? "portrait" : "landscape"}
        sunrunPercent={0}
        gaugePercent={utility.gaugePercent}
        gaugeLabel={utility.gaugeLabel}
        providerName={utilityCompany}
        recapBadge={utility.recapBadge}
        headline={utility.headline}
        bodyLines={utility.bodyLines}
        featureList={CAP_COMPARISON_FEATURE_LIST}
      />
      <SectionTitle
        size={32}
        fontWeight="regular"
        color={tokens.BRAND_HERO_BLUE}
      >
        After Sunrun
      </SectionTitle>
    </InfographicSection>
  );

  if (isLoading) {
    return (
      <InfographicStack>
        <SectionTitleSkeleton />
        <CardSkeleton />
        {mode === "single" ? (
          <>
            <SectionTitleSkeleton />
            <CardSkeleton />
          </>
        ) : (
          <ComparisonRow>
            <CardSkeleton $portrait />
            <CardSkeleton $portrait />
          </ComparisonRow>
        )}
      </InfographicStack>
    );
  }

  if (mode === "comparison") {
    return (
      <InfographicStack data-testid="cap-design-comparison-infographics">
        {beforeSunrunSection}
        <ComparisonRow>
          <InfographicSection
            data-testid="cap-comparison-original-offer"
            data-proposal-id={originalProposalId}
          >
            <OfferReviewComparisonInfographicCard
              variant="solar"
              layout="portrait"
              designLabel="Original"
              sunrunPercent={originalSelfSupplyPercent}
              gaugePercent={originalSelfSupplyPercent}
              gaugeLabel=""
              recapBadge={CAP_SOLAR_RECAP.recapBadge}
              headline={CAP_SOLAR_RECAP.headline}
              bodyLines={CAP_SOLAR_RECAP.bodyLines}
              featureList={CAP_COMPARISON_FEATURE_LIST}
            />
          </InfographicSection>
          <InfographicSection
            data-testid="cap-comparison-final-offer"
            data-proposal-id={activeProposalId}
          >
            <OfferReviewComparisonInfographicCard
              variant="solar"
              layout="portrait"
              designLabel="Final design"
              sunrunPercent={activeSelfSupplyPercent}
              gaugePercent={activeSelfSupplyPercent}
              gaugeLabel=""
              recapBadge={CAP_SOLAR_RECAP.recapBadge}
              headline={CAP_SOLAR_RECAP.headline}
              bodyLines={CAP_SOLAR_RECAP.bodyLines}
              featureList={CAP_COMPARISON_FEATURE_LIST}
            />
          </InfographicSection>
        </ComparisonRow>
      </InfographicStack>
    );
  }

  return (
    <InfographicStack data-testid="cap-design-single-infographics">
      {beforeSunrunSection}
      <InfographicSection>
        <OfferReviewComparisonInfographicCard
          variant="solar"
          layout="landscape"
          designLabel="Final design"
          sunrunPercent={activeSelfSupplyPercent}
          gaugePercent={activeSelfSupplyPercent}
          gaugeLabel=""
          recapBadge={CAP_SOLAR_RECAP.recapBadge}
          headline={CAP_SOLAR_RECAP.headline}
          bodyLines={CAP_SOLAR_RECAP.bodyLines}
          featureList={CAP_COMPARISON_FEATURE_LIST}
        />
      </InfographicSection>
    </InfographicStack>
  );
};

const InfographicStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  margin-bottom: 32px;
`;

const InfographicSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SectionTitle = styled(Typography)`
  && {
    line-height: 1.2;
    letter-spacing: -0.64px;
  }
`;

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const CardSkeleton = styled(Skeleton)<{ $portrait?: boolean }>`
  width: 100%;
  min-height: ${({ $portrait }) => ($portrait ? "520px" : "439px")};
  border-radius: 10px;
`;

const SectionTitleSkeleton = styled(Skeleton)`
  width: 240px;
  height: 38px;
`;

export { CapDesignStepComparisonInfographics };
